import {FC, useState} from "react";
import {Box, BoxProps, IconButton, List, ListItem, ListItemText} from "@mui/material";
import {makeStyles} from "tss-react/mui";
import {Add, Delete} from "@mui/icons-material";
import {normalizeLessonLinkUrl} from "@/utils/common-utilities";
import {AdminInputText} from "./AdminInputText";

const useStyles = makeStyles()((theme) => ({
	attachLinksRoot: {},
	linkInputRow: {
		display: "flex",
		gap: "12px",
		marginBottom: "20px",
		alignItems: "center",
	},
	linkList: {
		display: "flex",
		flexDirection: "column",
		gap: "8px",
		padding: "10px 0",
		"& li:hover": {
			background: theme.palette.grey[200],
			cursor: "pointer"
		},
		"li": {
			borderRadius: "5px",
			background: theme.palette.grey[100],
		}
	},
	deleteIcon: {
		"& svg path": {
			fill: theme.palette.error.main,
			cursor: "pointer"
		}
	}
}));

interface IProps extends Omit<BoxProps, "onChange"> {
	links: string[];
	onChange: (links: string[]) => void;
	/** Form-level validation message (e.g. on save). */
	error?: string;
	/** Called when the add field text changes (e.g. clear form-level link errors). */
	onLinkInputChange?: () => void;
}

export const AttachLinks: FC<IProps> = ({ links, onChange, error: errorFromParent, onLinkInputChange, className, ...props }) => {
	const { cx, classes } = useStyles();
	const [input, setInput] = useState("");
	const [addError, setAddError] = useState("");

	const addLink = () => {
		const normalized = normalizeLessonLinkUrl(input);
		if (!normalized) {
			setAddError("Enter a valid URL (http:// or https://).");
			return;
		}
		setAddError("");
		const updated = [...links, normalized];
		onChange(updated);
		setInput("");
	};

	const inputError = errorFromParent ?? addError;

	const removeLink = (index: number) => {
		const updated = links.filter((_, i) => i !== index);
		onChange(updated);
	};

	return (
		<Box className={cx(classes.attachLinksRoot, className)} {...props}>
			<Box className={classes.linkInputRow}>
				<AdminInputText
					onChange={(e) => {
						setInput(e.target.value);
						setAddError("");
						onLinkInputChange?.();
					}}
					value={input}
					label={"Add Link"}
					error={inputError}
				/>
				<IconButton onClick={addLink}>
					<Add />
				</IconButton>
			</Box>
			<List className={classes.linkList}>
				{links.map((link, i) => (
					<ListItem
						key={i}
						secondaryAction={
							<IconButton
								edge="end"
								className={classes.deleteIcon}
								onClick={() => removeLink(i)}
							>
								<Delete />
							</IconButton>
						}
					>
						<ListItemText primary={link} />
					</ListItem>
				))}
			</List>
		</Box>
	);
};
